import { useTheme } from "@/context/ThemeContext";
import { Monitor, Sun, Moon, Check } from "lucide-react";
import { toast } from "sonner";

// v77.20 — Appearance preference card.
//
// User-level setting (stored on the user document). Three options:
//   • Use device setting — follows OS / browser colour scheme live.
//   • Light mode — always light.
//   • Dark mode — always dark.
//
// The selection is applied across the whole app via the ThemeProvider
// which sets `data-theme` on <html>. Default is "system".
const OPTIONS = [
  {
    value: "system",
    label: "Use device setting",
    description: "Follow your phone or browser's appearance setting automatically.",
    Icon: Monitor,
  },
  {
    value: "light",
    label: "Light mode",
    description: "Bright surfaces. Best for sunny rooms and outdoors.",
    Icon: Sun,
  },
  {
    value: "dark",
    label: "Dark mode",
    description: "Easy on the eyes for evenings and low-light reading.",
    Icon: Moon,
  },
];

export function AppearanceCard() {
  const { preference, resolved, setPreference } = useTheme();

  const choose = async (next) => {
    if (next === preference) return;
    await setPreference(next);
    toast.success(
      next === "system"
        ? "Following your device setting"
        : `Switched to ${next} mode`,
      { duration: 2200 },
    );
  };

  return (
    <div
      className="bg-white border border-[#D9E1E3] rounded-md p-5"
      data-testid="appearance-card"
    >
      <div className="mb-4">
        <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">Appearance</div>
        <h2 className="font-heading text-lg font-medium text-[#2E3A3F] mt-1">Light or dark mode</h2>
        <p className="text-sm text-[#6F828A] mt-1">
          Currently showing{" "}
          <span className="font-medium text-[#2E3A3F]">
            {resolved === "dark" ? "dark" : "light"}
          </span>
          {preference === "system" ? " (following device)" : ""}.
        </p>
      </div>

      <div
        role="radiogroup"
        aria-label="Appearance preference"
        className="grid grid-cols-1 sm:grid-cols-3 gap-3"
      >
        {OPTIONS.map(({ value, label, description, Icon }) => {
          const active = preference === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => choose(value)}
              data-testid={`appearance-option-${value}`}
              data-active={active ? "true" : "false"}
              className={`relative text-left border rounded-md p-4 transition-all min-h-[110px] flex flex-col gap-2 focus:outline-none focus:ring-2 focus:ring-[#1F9FA4] focus:ring-offset-2 ${
                active
                  ? "border-[#1F9FA4] bg-[#F2FAFA] ring-1 ring-[#1F9FA4]"
                  : "border-[#D9E1E3] bg-white hover:border-[#A7C5DA] hover:bg-[#F4FAFA]"
              }`}
            >
              {active && (
                <span
                  className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#1F9FA4] text-white"
                  aria-hidden="true"
                >
                  <Check className="w-3 h-3" strokeWidth={2.5} />
                </span>
              )}
              <span
                className={`inline-flex w-8 h-8 rounded-sm items-center justify-center ${
                  active ? "bg-[#1F9FA4] text-white" : "bg-[#EEF3F4] text-[#116B6E]"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.6} />
              </span>
              <span className="font-medium text-[#2E3A3F] text-sm">{label}</span>
              <span className="text-xs text-[#6F828A] leading-relaxed">{description}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default AppearanceCard;
